import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ViewTimelineOutlinedIcon from "@mui/icons-material/ViewTimelineOutlined";
import {
	Box,
	Button,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import { addDays, format, parseISO, subDays } from "date-fns";
import React, { useMemo } from "react";

// eslint-disable-next-line sort-imports
import { MonoText, planner, SectionLabel } from "../../planner-ui";
import { ManufacturingOrder } from "../utils/calculations";

interface ProductionPlanDayViewProps {
	manufacturingOrders: ManufacturingOrder[]
	selectedDate: Date
	onDateChange: (date: Date) => void
	workcenters: string[]
	onEditMO: (mo: ManufacturingOrder) => void
	onDeleteMO: (id: number) => void
	/** Opens the matched labor standard's operations for an order. */
	onViewOperations?: (mo: ManufacturingOrder) => void
	/** True when the order has a matched labor standard to show operations for. */
	hasOperations?: (mo: ManufacturingOrder) => boolean
}

const HOUR_WIDTH = 100;
const ROW_HEIGHT = 100;
const START_HOUR = 6; // 6 AM
const END_HOUR = 24; // 12 AM (midnight)

// Block geometry. Two orders that start at the same minute on the same work
// center are stacked in LANES instead of drawn on top of each other, so the row
// grows downward by one lane per collision (see packIntoLanes).
const BLOCK_HEIGHT = 90;
const BLOCK_TOP = 5;
const LANE_GAP = 8;
const MIN_BLOCK_WIDTH = 120;
const GRID_WIDTH = (END_HOUR - START_HOUR) * HOUR_WIDTH;

const laneTop = (lane: number) => BLOCK_TOP + lane * (BLOCK_HEIGHT + LANE_GAP);
const rowHeightForLanes = (laneCount: number) =>
	Math.max(ROW_HEIGHT, laneTop(Math.max(1, laneCount) - 1) + BLOCK_HEIGHT + BLOCK_TOP);

// Generate hour columns from 6 AM to 11 PM
const generateHours = () => {
	const hours = [];
	for (let h = START_HOUR; h < END_HOUR; h++) {
		hours.push(h);
	}
	return hours;
};

// Parse the sku field into a bold headline + monospaced detail line
// (mirrors the week-view card treatment for visual coherence)
const parseMOTitle = (mo: ManufacturingOrder): { headline: string; detail: string | null } => {
	const raw = (mo.sku || "").trim();
	if (/\s\/\s/.test(raw)) {
		const segments = raw.split(/\s\/\s/).map((segment) => segment.trim()).filter(Boolean);
		if (segments.length > 1) {
			return { headline: segments[0], detail: segments.slice(1).join(" · ") };
		}
	}
	return {
		headline: raw || mo.productName || mo.productItemNumber || "Untitled order",
		detail: null,
	};
};

// Hours from the selected day's midnight (local time). Negative when the
// time is on an earlier day, above 24 when it is on a later one — a step that
// spans shifts/days is positioned from its real start and clipped by the grid.
const getHourOffset = (timeString: string, baseDate: Date): number => {
	const dayStart = new Date(baseDate);
	dayStart.setHours(0, 0, 0, 0);
	return (new Date(timeString).getTime() - dayStart.getTime()) / (1000 * 60 * 60);
};

// Effective rendered horizontal range of a block in pixels. Blocks are widened
// to MIN_BLOCK_WIDTH and pulled back inside the grid, so packing has to use the
// rendered range — not the raw hours — or minimum-width blocks still collide.
const getRenderedRange = (startHour: number, endHour: number) => {
	const width = Math.max(MIN_BLOCK_WIDTH, (endHour - startHour) * HOUR_WIDTH);
	const rawLeft = Math.max(0, (startHour - START_HOUR) * HOUR_WIDTH);
	const left = Math.min(rawLeft, Math.max(0, GRID_WIDTH - width));
	return { left, width };
};

interface PlacedBlock {
	mo: ManufacturingOrder
	startHour: number
	endHour: number
	left: number
	width: number
	/** Started before the visible window (earlier day or before 06:00). */
	continuesFrom: boolean
	/** Runs past the end of the visible window (24:00). */
	continuesTo: boolean
}

// Pack blocks into lanes (stacked rows) within one work-center row: a block goes
// on the first lane whose blocks it does not overlap, otherwise a new lane is
// added. Same approach the roster's MO dialog uses for overlapping phases.
const packIntoLanes = (blocks: PlacedBlock[]): PlacedBlock[][] => {
	const lanes: PlacedBlock[][] = [];
	const ordered = [...blocks].sort(
		(a, b) => a.left - b.left || a.width - b.width
	);

	ordered.forEach((block) => {
		const right = block.left + block.width;
		let placed = false;

		for (const lane of lanes) {
			const hasOverlap = lane.some((other) => {
				const otherRight = other.left + other.width;
				return !(right <= other.left || block.left >= otherRight);
			});

			if (!hasOverlap) {
				lane.push(block);
				placed = true;
				break;
			}
		}

		if (!placed) {
			lanes.push([block]);
		}
	});

	return lanes;
};

// MO Block Component
const MOBlock: React.FC<{
	mo: ManufacturingOrder
	startHour: number
	endHour: number
	left: number
	width: number
	top: number
	continuesFrom?: boolean
	continuesTo?: boolean
	onEdit: () => void
	onDelete: () => void
	onViewOperations?: () => void
	canViewOperations?: boolean
}> = ({
	mo,
	startHour,
	endHour,
	left,
	width,
	top,
	continuesFrom,
	continuesTo,
	onEdit,
	onDelete,
	onViewOperations,
	canViewOperations,
}) => {
	// Real planned duration, not the clipped on-screen span: a 20 h press step
	// reads "20.0h" on both of its days.
	const start = parseISO(mo.startTime);
	const end = mo.endTime ? parseISO(mo.endTime) : null;
	const duration = end ? (end.getTime() - start.getTime()) / 3600000 : endHour - startHour;

	// Times carry their weekday when the step crosses midnight
	const startTime = format(start, continuesFrom ? "EEE HH:mm" : "HH:mm");
	const endTimeStr = end ? format(end, continuesTo ? "EEE HH:mm" : "HH:mm") : "";
	const hasStandard = Boolean(mo.laborStandardId);
	const { headline, detail } = parseMOTitle(mo);
	const batchLabel = (mo.batchNumber || "").trim();

	return (
		<Box
			sx={{
				position: "absolute",
				left: `${left}px`,
				width: `${width}px`,
				minWidth: `${MIN_BLOCK_WIDTH}px`,
				height: `${BLOCK_HEIGHT}px`,
				top: `${top}px`,
				bgcolor: planner.surface,
				border: `${planner.borderWidth}px solid ${planner.hairline}`,
				borderLeft: continuesFrom
					? `${planner.borderWidth}px dashed ${planner.inkMuted}`
					: hasStandard
						? `${planner.borderWidth}px solid ${planner.hairline}`
						: `3px solid ${planner.unplanned}`,
				borderRight: continuesTo
					? `${planner.borderWidth}px dashed ${planner.inkMuted}`
					: `${planner.borderWidth}px solid ${planner.hairline}`,
				// A clipped edge is square: the block visibly runs off the grid
				borderTopLeftRadius: continuesFrom ? 0 : `${planner.radiusSm}px`,
				borderBottomLeftRadius: continuesFrom ? 0 : `${planner.radiusSm}px`,
				borderTopRightRadius: continuesTo ? 0 : `${planner.radiusSm}px`,
				borderBottomRightRadius: continuesTo ? 0 : `${planner.radiusSm}px`,
				boxShadow: planner.shadowRest,
				p: 1,
				display: "flex",
				flexDirection: "column",
				cursor: "pointer",
				zIndex: 10,
				overflow: "hidden",
				transition: "border-color 150ms ease",
				"&:hover": { borderColor: planner.navy },
				"&:hover .mo-block-actions": { opacity: 1 },
			}}
			onClick={(e) => {
				e.stopPropagation();
				onEdit();
			}}
		>
			<Typography
				sx={{
					...planner.type.bodyStrong,
					color: planner.ink,
					lineHeight: 1.25,
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
					pr: 5,
				}}
			>
				{continuesFrom ? (
					<Tooltip title={`Continues from ${format(start, "EEE HH:mm")}`}>
						<Box
							component="span"
							sx={{
								...planner.type.meta,
								fontFamily: planner.fontMono,
								color: planner.inkMuted,
								mr: 0.5,
							}}
						>
							◂ cont.
						</Box>
					</Tooltip>
				) : null}
				{headline}
			</Typography>
			{batchLabel ? (
				<MonoText
					sx={{
						display: "block",
						mt: 0.25,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					Batch {batchLabel}
				</MonoText>
			) : null}
			{detail ? (
				<MonoText
					sx={{
						display: "block",
						mt: 0.25,
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					}}
				>
					{detail}
				</MonoText>
			) : (
				mo.productName && mo.productName !== headline && (
					<Typography
						sx={{
							...planner.type.meta,
							color: planner.inkSecondary,
							mt: 0.25,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{mo.productName}
					</Typography>
				)
			)}
			<Typography
				sx={{
					...planner.type.metaStrong,
					mt: "auto",
					color: planner.inkSecondary,
					fontFamily: planner.fontMono,
					fontVariantNumeric: "tabular-nums",
					whiteSpace: "nowrap",
				}}
			>
				{startTime}
				{endTimeStr && `–${endTimeStr}`}
				{` · ${duration.toFixed(1)}h`}
				{mo.quantity > 1 && ` · ×${mo.quantity.toLocaleString()}`}
				{continuesTo && " ▸"}
			</Typography>
			<Box
				className="mo-block-actions"
				sx={{
					position: "absolute",
					top: 4,
					right: 4,
					display: "flex",
					gap: 0.25,
					opacity: 0,
					transition: "opacity 120ms ease",
					bgcolor: planner.surface,
					borderRadius: `${planner.radiusSm}px`,
				}}
			>
				{onViewOperations && (
					<Tooltip
						title={
							canViewOperations
								? "View operations"
								: "No labor standard matched — no operations to show"
						}
					>
						<span>
							<IconButton
								size="small"
								disabled={!canViewOperations}
								onClick={(e) => {
									e.stopPropagation();
									onViewOperations();
								}}
								sx={{
									p: 0.4,
									color: planner.inkMuted,
									"&:hover": { color: planner.ink },
									"&.Mui-disabled": { color: planner.hairlineStrong },
								}}
							>
								<ViewTimelineOutlinedIcon sx={{ fontSize: 14 }} />
							</IconButton>
						</span>
					</Tooltip>
				)}
				<Tooltip title="Edit">
					<IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							onEdit();
						}}
						sx={{ p: 0.4, color: planner.inkMuted, "&:hover": { color: planner.ink } }}
					>
						<EditOutlinedIcon sx={{ fontSize: 14 }} />
					</IconButton>
				</Tooltip>
				<Tooltip title="Delete">
					<IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							onDelete();
						}}
						sx={{ p: 0.4, color: planner.inkMuted, "&:hover": { color: planner.unstaffed } }}
					>
						<DeleteOutlineIcon sx={{ fontSize: 14 }} />
					</IconButton>
				</Tooltip>
			</Box>
		</Box>
	);
};

const ProductionPlanDayView: React.FC<ProductionPlanDayViewProps> = ({
	manufacturingOrders,
	selectedDate,
	onDateChange,
	workcenters,
	onEditMO,
	onDeleteMO,
	onViewOperations,
	hasOperations,
}) => {
	const hours = useMemo(() => generateHours(), []);

	// Filter MOs for the selected date
	const dayMOs = useMemo(() => {
		// Normalize selectedDate to start of day in local time
		const selectedDateStart = new Date(selectedDate);
		selectedDateStart.setHours(0, 0, 0, 0);
		const selectedDateEnd = new Date(selectedDateStart);
		selectedDateEnd.setHours(23, 59, 59, 999);

		// A step belongs to the day when it STARTS on it, or when it started
		// earlier and is still running at midnight (continuation of a spanning
		// step). Batch headers have no work center and never sit in a row.
		const filtered = manufacturingOrders.filter((mo) => {
			if (!mo.workstation) return false;
			const moStart = new Date(mo.startTime);
			if (moStart >= selectedDateStart && moStart <= selectedDateEnd) return true;
			const moEnd = mo.endTime ? new Date(mo.endTime) : null;
			return Boolean(moEnd && moStart < selectedDateStart && moEnd > selectedDateStart);
		});

		return filtered;
	}, [manufacturingOrders, selectedDate]);

	// Get MOs for a specific workcenter
	const getMOsForWorkcenter = (workcenter: string) => {
		return dayMOs.filter((mo) => mo.workstation === workcenter);
	};

	// Position of an MO block on the day's axis. A step that started before the
	// window (earlier day, or before 06:00) is clipped at the left edge and
	// flagged; one that runs past 24:00 is clipped at the right edge and flagged.
	const getMOPosition = (mo: ManufacturingOrder) => {
		const rawStartHour = getHourOffset(mo.startTime, selectedDate);
		const rawEndHour = mo.endTime
			? getHourOffset(mo.endTime, selectedDate)
			: rawStartHour + 8; // Default 8 hours if no end time

		const startHour = Math.max(START_HOUR, Math.min(rawStartHour, END_HOUR));
		const endHour = Math.max(START_HOUR, Math.min(rawEndHour, END_HOUR));

		// Entirely outside the visible range: pin a sliver at the nearest edge
		if (rawEndHour <= START_HOUR) {
			return { startHour: START_HOUR, endHour: START_HOUR + 0.5, continuesFrom: true, continuesTo: false };
		}
		if (rawStartHour >= END_HOUR) {
			return { startHour: END_HOUR - 0.5, endHour: END_HOUR, continuesFrom: false, continuesTo: true };
		}

		return {
			startHour,
			endHour,
			continuesFrom: rawStartHour < START_HOUR,
			continuesTo: rawEndHour > END_HOUR,
		};
	};

	const handlePreviousDay = () => {
		const prevDay = subDays(selectedDate, 1);
		onDateChange(prevDay);
	};

	const handleNextDay = () => {
		const nextDay = addDays(selectedDate, 1);
		onDateChange(nextDay);
	};

	const handleToday = () => {
		onDateChange(new Date());
	};

	return (
		// minWidth 0 keeps the max-content timeline rows from sizing this column
		// (and every flex ancestor up to the app shell) to the full grid width,
		// which would scroll the page header and horizon switcher off-screen.
		<Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
			{/* Date navigation toolbar */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					mb: 1.5,
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.25,
						border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
						borderRadius: `${planner.radiusSm}px`,
						bgcolor: planner.surface,
						px: 0.5,
						py: 0.25,
					}}
				>
					<Button
						size="small"
						onClick={handleToday}
						sx={{
							...planner.type.bodyStrong,
							textTransform: "none",
							color: planner.ink,
							minWidth: 0,
							px: 1,
						}}
					>
						Today
					</Button>
					<IconButton onClick={handlePreviousDay} size="small" sx={{ color: planner.inkSecondary }}>
						<ChevronLeftIcon fontSize="small" />
					</IconButton>
					<IconButton onClick={handleNextDay} size="small" sx={{ color: planner.inkSecondary }}>
						<ChevronRightIcon fontSize="small" />
					</IconButton>
				</Box>
				<Typography sx={{ ...planner.type.title, color: planner.ink }}>
					{format(selectedDate, "EEEE, MMMM d, yyyy")}
				</Typography>
				<Typography
					sx={{
						...planner.type.meta,
						color: planner.inkMuted,
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{dayMOs.length} job order{dayMOs.length !== 1 ? "s" : ""}
				</Typography>
			</Box>

			{/* Timeline grid */}
			<Box
				sx={{
					overflow: "auto",
					// Without this the container sizes to its max-content rows
					// instead of clipping them, so the scroll never engages.
					minWidth: 0,
					bgcolor: planner.surface,
					border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
					borderRadius: `${planner.radius}px`,
					boxShadow: planner.shadowRest,
				}}
			>
				{/* Hour Headers */}
				<Box
					sx={{
						display: "flex",
						borderBottom: `1px solid ${planner.hairline}`,
						position: "sticky",
						top: 0,
						zIndex: 12,
						bgcolor: planner.surface,
						minWidth: "max-content",
					}}
				>
					<Box
						sx={{
							width: 150,
							flexShrink: 0,
							borderRight: `1px solid ${planner.hairline}`,
							px: 1.5,
							py: 1.25,
							display: "flex",
							alignItems: "center",
							bgcolor: planner.surface,
							position: "sticky",
							left: 0,
							zIndex: 13,
						}}
					>
						<SectionLabel>Work center</SectionLabel>
					</Box>
					{hours.map((hour) => (
						<Box
							key={hour}
							sx={{
								width: HOUR_WIDTH,
								flexShrink: 0,
								borderRight: `1px solid ${planner.hairline}`,
								py: 1.25,
								textAlign: "center",
							}}
						>
							<Typography
								sx={{
									...planner.type.metaStrong,
									color: planner.inkSecondary,
									fontFamily: planner.fontMono,
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{format(new Date().setHours(hour, 0, 0, 0), "HH:mm")}
							</Typography>
						</Box>
					))}
				</Box>

				{/* Workcenter Rows */}
				{workcenters.map((workcenter) => {
					const wcMOs = getMOsForWorkcenter(workcenter);
					// Overlapping orders are stacked in lanes; the row grows to hold them
					const lanes = packIntoLanes(
						wcMOs.map((mo) => {
							const { startHour, endHour, continuesFrom, continuesTo } = getMOPosition(mo);
							const { left, width } = getRenderedRange(startHour, endHour);
							return { mo, startHour, endHour, left, width, continuesFrom, continuesTo };
						})
					);
					const rowHeight = rowHeightForLanes(lanes.length);

					return (
						<Box
							key={workcenter}
							sx={{
								display: "flex",
								borderBottom: `1px solid ${planner.hairline}`,
								minHeight: rowHeight,
								position: "relative",
								minWidth: "max-content",
								"&:last-of-type": { borderBottom: "none" },
							}}
						>
							{/* Workcenter Label */}
							<Box
								sx={{
									width: 150,
									flexShrink: 0,
									borderRight: `1px solid ${planner.hairline}`,
									px: 1.5,
									py: 1.5,
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									bgcolor: planner.surfaceSubtle,
									position: "sticky",
									left: 0,
									zIndex: 11,
								}}
							>
								<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink, lineHeight: 1.3 }}>
									{workcenter}
								</Typography>
								{wcMOs.length > 0 && (
									<Typography
										sx={{
											...planner.type.meta,
											color: planner.inkMuted,
											mt: 0.25,
											fontVariantNumeric: "tabular-nums",
										}}
									>
										{wcMOs.length} order{wcMOs.length !== 1 ? "s" : ""}
									</Typography>
								)}
							</Box>

							{/* Time Grid Cells */}
							<Box sx={{ position: "relative", flexShrink: 0, minHeight: rowHeight }}>
								{hours.map((hour) => (
									<Box
										key={hour}
										sx={{
											width: HOUR_WIDTH,
											height: rowHeight,
											borderRight: `1px solid ${planner.hairline}`,
											display: "inline-block",
											bgcolor: hour % 2 === 0 ? "transparent" : planner.surfaceSubtle,
										}}
									/>
								))}

								{/* MO Blocks — one row of blocks per lane */}
								{lanes.map((lane, laneIndex) =>
									lane.map((block) => (
										<MOBlock
											key={block.mo.id}
											mo={block.mo}
											startHour={block.startHour}
											endHour={block.endHour}
											left={block.left}
											width={block.width}
											top={laneTop(laneIndex)}
											continuesFrom={block.continuesFrom}
											continuesTo={block.continuesTo}
											onEdit={() => onEditMO(block.mo)}
											onDelete={() => onDeleteMO(block.mo.id)}
											onViewOperations={
												onViewOperations ? () => onViewOperations(block.mo) : undefined
											}
											canViewOperations={hasOperations ? hasOperations(block.mo) : false}
										/>
									))
								)}
							</Box>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};

export default ProductionPlanDayView;
